/**
 * `/gaps` — every company's gaps, and a way to fill several at once.
 *
 * Selecting companies to spend money on is a different task from browsing
 * them, which is why this is its own screen rather than checkboxes on the
 * card grid: the question here is *where is the work and what will it cost*,
 * and answering it needs the counts in front of you while you choose. The
 * grid has no gap counts and should not grow them.
 *
 * The rows sum to a live total in the footer, and that total is the whole
 * point. The corpus is 355 incomplete documents; a "select all" that did not
 * put ~51M tokens in front of someone before they pressed it would be a trap.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'wouter'
import { useMemo, useState } from 'react'
import type { CorpusGapsResponse, CorpusGapRow } from '@server/sql/gaps.ts'
import type { BatchEstimate } from '@server/jobs/estimate.ts'
import type { JobRecord } from '@server/jobs/store.ts'
import { ApiError, api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count } from '~/i18n/format.ts'
import { Button, Skeleton } from '~/components/ui/primitives.tsx'
import { EmptyState, ErrorState } from '~/components/ui/empty.tsx'
import { Table, Td, Th, Tr } from '~/components/ui/table.tsx'
import { Raw } from '~/components/ui/raw.tsx'
import { useSuperadmin } from '~/lib/superadmin.ts'
import { cn } from '~/lib/cn.ts'
import { BatchDialog } from '~/features/gaps/batch-dialog.tsx'
import { useDocumentTitle } from '~/lib/title.ts'

const TERMINAL = new Set(['done', 'failed', 'cancelled', 'interrupted'])

export function CorpusGapsScreen() {
  const { t, locale } = useT()
  useDocumentTitle(t('corpus.gaps.title'))
  const qc = useQueryClient()
  const superadmin = useSuperadmin()
  const [selected, setSelected] = useState<string[]>([])
  const [confirming, setConfirming] = useState(false)

  const { data, error, refetch } = useQuery({
    queryKey: ['gaps', 'corpus'],
    queryFn: () => api<CorpusGapsResponse>('/api/gaps'),
    staleTime: 30_000,
  })

  const { data: jobList } = useQuery({
    queryKey: keys.jobs(),
    queryFn: () => api<{ jobs: JobRecord[]; enabled: boolean; maxTokens: number }>('/api/jobs'),
    refetchInterval: 10_000,
  })
  const actionsEnabled = jobList?.enabled ?? false
  const maxTokens = jobList?.maxTokens ?? 0

  /** Symbols with work already in flight; selecting them would be a no-op. */
  const busy = useMemo(
    () => new Set((jobList?.jobs ?? []).filter((j) => !TERMINAL.has(j.status)).map((j) => j.symbol)),
    [jobList],
  )

  const start = useMutation({
    mutationFn: () =>
      api<{ batchId: string; jobs: JobRecord[]; skipped: { symbol: string; why: string }[] }>(
        '/api/jobs/batch',
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ kind: 'fill_company', symbols: selected }),
        },
      ),
    onSuccess: () => {
      setSelected([])
      setConfirming(false)
      void qc.invalidateQueries({ queryKey: keys.jobs() })
      void qc.invalidateQueries({ queryKey: ['gaps'] })
    },
  })

  if (!superadmin)
    return (
      <Column>
        <EmptyState title={t('empty.generic')}>
          <Link href="/results" className="link-inline text-[var(--accent)]">{t('nav.results')}</Link>
        </EmptyState>
      </Column>
    )

  if (error)
    return (
      <Column>
        <ErrorState
          title={t('common.error')} detail={(error as ApiError).message}
          onRetry={() => void refetch()} retryLabel={t('action.retry')}
        />
      </Column>
    )
  if (!data) return <Column><Skeleton className="h-96 w-full" /></Column>

  const rows = data.companies
  const n = (x: number) => count(x, locale)
  const toggle = (sym: string) =>
    setSelected((s) => (s.includes(sym) ? s.filter((x) => x !== sym) : [...s, sym]))

  const selectable = rows.filter((r) => !busy.has(r.symbol))
  const totals = rows
    .filter((r) => selected.includes(r.symbol))
    .reduce(
      (a, r) => ({
        incomplete: a.incomplete + r.incomplete,
        review: a.review + r.openReview,
        planned: a.planned + (r.plannedTokens ?? 0),
      }),
      { incomplete: 0, review: 0, planned: 0 },
    )

  // Checked here rather than only on the server: being told "too much" after
  // pressing the button, in a language the rest of the screen is not in, is
  // the failure this replaces. The server still enforces it.
  const overCap = maxTokens > 0 && totals.planned > maxTokens

  if (data.totals.incomplete === 0 && data.totals.openReview === 0)
    return <Column><EmptyState title={t('corpus.gaps.allDone')} /></Column>

  return (
    <Column>
      <header className="pb-3">
        <h1 className="text-[length:var(--text-display)] font-semibold tracking-tight">
          {t('corpus.gaps.title')}
        </h1>
        <p className="mt-1 max-w-[70ch] text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--text-muted)]">
          {t('corpus.gaps.subtitle')}
        </p>
        <div className="tnum mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[length:var(--text-data)] text-[var(--text-muted)]">
          <span className="text-[var(--pass)]">{t('gaps.summary.complete', { n: n(data.totals.parsed) })}</span>
          <Sep />
          <span className="text-[var(--fail)]">{t('gaps.summary.missing', { n: n(data.totals.neverRead) })}</span>
          <Sep />
          <span className="text-[var(--fail)]">{t('gaps.summary.stalled', { n: n(data.totals.stalled) })}</span>
          <Sep />
          <span className="text-[var(--warn)]">
            {n(data.totals.openReview)} {t('corpus.gaps.review').toLowerCase()}
          </span>
        </div>
      </header>

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => setSelected(selectable.map((r) => r.symbol))}>
          {t('corpus.gaps.selectAll')}
        </Button>
        <Button
          size="sm" variant="ghost"
          onClick={() => setSelected(selectable.filter((r) => r.incomplete > 0).map((r) => r.symbol))}
        >
          {t('corpus.gaps.selectIncomplete')}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setSelected([])}>
          {t('corpus.gaps.selectNone')}
        </Button>
      </div>

      <Table>
        <thead>
          <Tr>
            <Th className="w-8" />
            <Th>{t('corpus.gaps.company')}</Th>
            <Th numeric>{t('corpus.gaps.parsed')}</Th>
            <Th numeric>{t('corpus.gaps.neverRead')}</Th>
            <Th numeric>{t('corpus.gaps.stalled')}</Th>
            <Th numeric>{t('corpus.gaps.review')}</Th>
            <Th numeric>{t('corpus.gaps.planned')}</Th>
          </Tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Row
              key={r.symbol} row={r}
              checked={selected.includes(r.symbol)}
              busy={busy.has(r.symbol)}
              onToggle={() => toggle(r.symbol)}
            />
          ))}
        </tbody>
      </Table>

      {/*
        Sticky, because the numbers that decide this are the summed ones and
        the table is thirteen rows now and will not always be.
      */}
      <div
        className={cn(
          'sticky bottom-0 z-10 mt-3 flex flex-wrap items-center gap-x-3 gap-y-2',
          'rounded-[var(--radius-container)] border bg-[var(--bg-raised)] px-3 py-2.5',
          selected.length > 0 ? 'border-[var(--accent)]/40' : 'border-[var(--border)]',
        )}
      >
        <span className="tnum text-[length:var(--text-data)] font-medium text-[var(--text)]">
          {selected.length === 0
            ? t('corpus.gaps.nothing')
            : t('corpus.gaps.selected', { n: n(selected.length) })}
        </span>
        {selected.length > 0 && (
          <span className="tnum text-[length:var(--text-data)] text-[var(--text-muted)]">
            {n(totals.incomplete)} {t('corpus.gaps.incomplete').toLowerCase()}
            <Sep />
            {n(totals.planned)} {t('corpus.gaps.planned').toLowerCase()}
          </span>
        )}
        {maxTokens > 0 && selected.length > 0 && (
          <span className={cn(
            'tnum text-[length:var(--text-micro)]',
            overCap ? 'text-[var(--fail)]' : 'text-[var(--text-faint)]',
          )}>
            {t('corpus.gaps.capRoom', { n: n(totals.planned), cap: n(maxTokens) })}
          </span>
        )}
        <span className="text-[length:var(--text-micro)] text-[var(--text-faint)]">
          {t('corpus.gaps.queueNote')}
        </span>
        <div className="flex-1" />
        {start.error && (
          <span className="text-[length:var(--text-micro)] text-[var(--fail)]">
            {(start.error as ApiError).message}
          </span>
        )}
        <Button
          variant="primary"
          disabled={selected.length === 0 || overCap}
          onClick={() => setConfirming(true)}
        >
          {t('corpus.gaps.runSelected', { n: n(selected.length) })}
        </Button>
      </div>

      {overCap && (
        <p className="mt-2 rounded-[var(--radius-control)] border border-[var(--fail)]/30 bg-[var(--fail-bg)] px-2.5 py-1.5 text-[length:var(--text-data)] leading-[var(--text-data--line-height)] text-[var(--fail)]">
          {t('corpus.gaps.overCapFull', { n: n(totals.planned), cap: n(maxTokens) })}
        </p>
      )}

      <BatchDialog
        symbols={selected}
        open={confirming}
        enabled={actionsEnabled}
        pending={start.isPending}
        maxTokens={maxTokens}
        onClose={() => setConfirming(false)}
        onConfirm={() => start.mutate()}
      />
    </Column>
  )
}

function Row({ row, checked, busy, onToggle }: {
  row: CorpusGapRow; checked: boolean; busy: boolean; onToggle: () => void
}) {
  const { t, locale } = useT()
  const n = (x: number) => count(x, locale)
  return (
    <Tr className={cn(checked && 'bg-[var(--accent-soft)]')}>
      <Td>
        <input
          type="checkbox"
          checked={checked}
          disabled={busy}
          onChange={onToggle}
          aria-label={row.symbol}
          className="size-4 accent-[var(--accent)] disabled:opacity-40"
        />
      </Td>
      <Td>
        <Link href={`/c/${row.symbol}?tab=gaps`} className="link-inline">
          <span className="tnum font-[family-name:var(--font-mono)] text-[var(--text-muted)]">
            {row.symbol}
          </span>{' '}
          <span className="text-[var(--text)]">{row.nameEn ?? row.symbol}</span>
        </Link>
        {row.nameAr && (
          <Raw lang="ar" className="ms-2 text-[length:var(--text-micro)] text-[var(--text-faint)]">
            {row.nameAr}
          </Raw>
        )}
        {busy && (
          <span className="ms-2 text-[length:var(--text-micro)] text-[var(--accent)]">
            {t('corpus.gaps.running')}
          </span>
        )}
      </Td>
      <Td numeric className="text-[var(--text-muted)]">{n(row.parsed)}</Td>
      <Td numeric className={row.neverRead > 0 ? 'text-[var(--fail)]' : 'text-[var(--text-faint)]'}>
        {row.neverRead > 0 ? n(row.neverRead) : '—'}
      </Td>
      <Td numeric className={row.stalled > 0 ? 'text-[var(--fail)]' : 'text-[var(--text-faint)]'}>
        {row.stalled > 0 ? n(row.stalled) : '—'}
      </Td>
      <Td numeric className={row.openReview > 0 ? 'text-[var(--warn)]' : 'text-[var(--text-faint)]'}>
        {row.openReview > 0 ? n(row.openReview) : '—'}
      </Td>
      <Td numeric className="text-[var(--text-muted)]">
        {row.plannedTokens === null ? '—' : n(row.plannedTokens)}
      </Td>
    </Tr>
  )
}

const Sep = () => <span className="mx-1.5 text-[var(--border-strong)]">·</span>

function Column({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-[84rem] px-4 pb-10 pt-5 sm:px-5 2xl:max-w-[110rem]">
      {children}
    </div>
  )
}
